import React, { useState, useContext } from 'react';
import { FaEye, FaClock, FaEllipsisV } from 'react-icons/fa';
import { TbTriangle, TbTriangleFilled, TbTriangleInverted, TbTriangleInvertedFilled } from "react-icons/tb";
import AuthContext from '../../contexts/AuthContext';
import { mediaTypes } from '../../constants/mediaTypes';
import { MdVideoLibrary } from "react-icons/md";
import './LearningMaterialCard.css';
import { toast } from 'react-toastify';
import { RiImage2Fill } from "react-icons/ri";

const LearningMaterialCard = ({ imgSrc, title, timeEstimate, views, description, upVotes, downVotes, referenceLink, referenceId, userVote, mediaType }) => {

    const { user } = useContext(AuthContext);

    const [localUpVotes, setLocalUpVotes] = useState(upVotes);
    const [localDownVotes, setLocalDownVotes] = useState(downVotes);
    const [voteStatus, setVoteStatus] = useState(userVote); // 'upvote', 'downvote', or null

    // TODO: Perhaps it is better to make this globally accessible?
    const mediaTypeInfo =
        mediaTypes.find((type) => type.id === Number(mediaType)) || {
            name: 'Unknown',
            icon: null,
        };

    const handleInteractiveClick = (e) => {
        e.stopPropagation();
        incrementViewCount();
    };

    // State to handle image load errors
    const [imageError, setImageError] = useState(false);

    const handleVote = async (voteType) => {
        if (!user) {
            toast.info('Please log in to vote.', {
                toastId: 'login-required', // Unique identifier for this toast
            });
            return;
        }
        try {
            const response = await fetch(`/api/reference/vote`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ voteType, referenceId })
            });
            if (response.ok) {
                const { updatedReference } = await response.json();
                setLocalUpVotes(updatedReference.upVotes);
                setLocalDownVotes(updatedReference.downVotes);
                setVoteStatus(voteType === voteStatus ? null : voteType); // Toggle vote off or switch to new vote type
            } else {
                throw new Error('Failed to update vote');
            }
        } catch (error) {
            console.error('Error updating vote:', error);
        }
    };

    const handleUpvote = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleVote('upvote');
    };

    const handleDownvote = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleVote('downvote');
    };

    const formatTimeEstimate = (minutes) => {
        if (minutes === 0) return '-';

        const hours = minutes / 60;
        const days = hours / 24;

        if (days >= 1) {
            const roundedDays = Math.round(days * 10) / 10;
            return `${roundedDays % 1 === 0 ? Math.round(roundedDays) : roundedDays} day${roundedDays !== 1 ? 's' : ''}`;
        } else if (hours >= 1) {
            const roundedHours = Math.round(hours * 10) / 10;
            return `${roundedHours % 1 === 0 ? Math.round(roundedHours) : roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
        } else {
            return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
        }
    };

    // TODO maybe move to utils
    const formatVotes = () => {
        const netVotes = localUpVotes - localDownVotes;
        if (Math.abs(netVotes) >= 1000) {
            return `${Math.sign(netVotes) * (Math.abs(netVotes) / 1000).toFixed(1).replace(/\.0$/, '')}k`;
        }
        return `${netVotes}`;
    };

    const formatViews = (views) => {
        if (views >= 1000) {
            return `${(views / 1000).toFixed(1).replace(/\.0$/, '')}k`;
        }
        return `${views}`;
    };

    const incrementViewCount = async () => {
        try {
            const response = await fetch(`/api/reference/incrementViews`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ referenceId })
            });
            if (!response.ok) {
                throw new Error('Failed to increment view count');
            }
        } catch (error) {
            console.error('Error incrementing view count:', error);
        }
    };

    return (
        <div className="reference-card card rounded">
            <a href={referenceLink} target="_blank" rel="noopener noreferrer" className="d-flex" onClick={handleInteractiveClick}>
                <div className="reference-image">
                    {!imageError && imgSrc ? (
                        <img
                            className="card-image"
                            src={imgSrc}
                            alt={title}
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        <div className="placeholder-image">
                            {mediaTypeInfo.icon ? (
                                <div className="placeholder-icon">{mediaTypeInfo.icon}</div>
                            ) : (
                                <div className="placeholder-icon"><RiImage2Fill/></div>
                            )}
                        </div>
                    )}
                </div>
                <div className="reference-card-main d-flex flex-column justify-content-between">
                    <div>
                        <h5 className="reference-title font-3" title={title}>{title}</h5>
                        <div className="reference-details font-5">
                            {/*<span className="detail-item"><FaClock className="icon-align" /> {formatTimeEstimate(timeEstimate)}</span>*/}
                            <span className="detail-item"><FaEye className="icon-align" /> {formatViews(views)}</span>
                            {mediaTypeInfo.icon && (
                                <span className="detail-item">
                                    <span className="icon-align" style={{ display: "inline-block" }}>{mediaTypeInfo.icon}</span> {mediaTypeInfo.name}
                                </span>
                            )}
                        </div>
                        {/*<p className="reference-description font-4">{description}</p>*/}
                    </div>
                    <div className="d-flex justify-content-between align-items-center margin-top-15">
                        <div className="vote-section">
                            <button
                                className={`btn vote-btn upvote ${voteStatus === 'upvote' ? 'upvoted' : ''}`}
                                onClick={handleUpvote}
                            >
                                {voteStatus === 'upvote' ? <TbTriangleFilled className="icon-align" /> : <TbTriangle className="icon-align" />}
                            </button>
                            <span className={`vote-count ${voteStatus === 'upvote' ? 'upvoted' : voteStatus === 'downvote' ? 'downvoted' : ''}`}>
                                {formatVotes()}
                            </span>
                            <button
                                className={`btn vote-btn downvote ${voteStatus === 'downvote' ? 'downvoted' : ''}`}
                                onClick={handleDownvote}
                            >
                                {voteStatus === 'downvote' ? <TbTriangleInvertedFilled className="icon-align" /> : <TbTriangleInverted className="icon-align" />}
                            </button>
                        </div>
                        {/*<button className="btn ellipsis-btn">*/}
                        {/*    <FaEllipsisV />*/}
                        {/*</button>*/}
                    </div>
                </div>
            </a>
        </div>
    );
};

export default LearningMaterialCard;
